'use client'

import { useState, useMemo } from 'react'

interface Props {
  historicalPrices: [number, number][] // [timestamp, price]
  currentPrice: number
  currencySymbol: string
}

export function TimeMachineSlider({ historicalPrices, currentPrice, currencySymbol }: Props) {
  const [sliderValue, setSliderValue] = useState(0)

  // Sample to ~365 points max, newest first
  const sampled = useMemo(() => {
    if (historicalPrices.length <= 365) return [...historicalPrices].sort((a, b) => b[0] - a[0])
    const step = Math.floor(historicalPrices.length / 365)
    const s = historicalPrices.filter((_, i) => i % step === 0 || i === historicalPrices.length - 1)
    return s.sort((a, b) => b[0] - a[0])
  }, [historicalPrices])

  const maxIdx = sampled.length - 1

  const selectedPoint = sampled[Math.min(sliderValue, maxIdx)]
  const thenPrice = selectedPoint?.[1] ?? currentPrice
  const selectedDate = selectedPoint ? new Date(selectedPoint[0]) : new Date()

  const diff = currentPrice - thenPrice
  const pctChange = thenPrice > 0 ? (diff / thenPrice) * 100 : 0
  const isUp = diff >= 0
  const isToday = sliderValue === 0

  // Days ago
  const daysAgo = Math.round((Date.now() - selectedDate.getTime()) / 86400000)

  // Bar widths: bigger price = 100%, smaller scales proportionally
  const maxPrice = Math.max(thenPrice, currentPrice, 0.0001)
  const thenWidth = (thenPrice / maxPrice) * 100
  const nowWidth = (currentPrice / maxPrice) * 100

  const fmtPrice = (p: number) => {
    if (p >= 1000) return `${currencySymbol}${p.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    if (p >= 1) return `${currencySymbol}${p.toFixed(2)}`
    if (p >= 0.01) return `${currencySymbol}${p.toFixed(4)}`
    return `${currencySymbol}${p.toPrecision(4)}`
  }

  const fmtDiff = (d: number) => {
    const abs = Math.abs(d)
    if (abs >= 1000) return `${currencySymbol}${abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    if (abs >= 1) return `${currencySymbol}${abs.toFixed(2)}`
    if (abs >= 0.01) return `${currencySymbol}${abs.toFixed(4)}`
    return `${currencySymbol}${abs.toPrecision(4)}`
  }

  const dateStr = selectedDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  const daysLabel = daysAgo === 0 ? 'Today' : daysAgo === 1 ? '1 day ago' : `${daysAgo} days ago`

  return (
    <div className="border border-zinc-800 px-4 sm:px-5 py-5">
      {/* Date header */}
      <div className="text-center mb-4">
        <p className="text-zinc-400 text-[10px] uppercase tracking-wider">
          {isToday ? `Today \u00B7 ${dateStr}` : `${dateStr} \u00B7 ${daysLabel}`}
        </p>
      </div>

      {/* Then vs Now bars */}
      <div className="mb-4 space-y-1.5">
        {/* Then bar */}
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="w-8 sm:w-10 text-right text-zinc-400 text-[9px] sm:text-[10px] uppercase flex-shrink-0">
            Then
          </span>
          <div className="flex-1 h-5 bg-zinc-950 rounded-sm overflow-hidden">
            <div
              className="h-full rounded-sm flex items-center justify-end px-2 transition-all duration-300"
              style={{
                width: `${Math.max(thenWidth, 15)}%`,
                background: 'rgba(161,161,170,0.15)',
              }}
            >
              <span className="text-zinc-300 text-[10px] sm:text-[11px] font-semibold tabular-nums whitespace-nowrap">
                {fmtPrice(thenPrice)}
              </span>
            </div>
          </div>
        </div>

        {/* Now bar */}
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="w-8 sm:w-10 text-right text-zinc-400 text-[9px] sm:text-[10px] uppercase flex-shrink-0">
            Now
          </span>
          <div className="flex-1 h-5 bg-zinc-950 rounded-sm overflow-hidden">
            <div
              className="h-full rounded-sm flex items-center justify-end px-2 transition-all duration-300"
              style={{
                width: `${Math.max(nowWidth, 15)}%`,
                background: isToday
                  ? 'rgba(161,161,170,0.1)'
                  : isUp
                    ? 'rgba(34,197,94,0.2)'
                    : 'rgba(239,68,68,0.2)',
              }}
            >
              <span className="text-white text-[10px] sm:text-[11px] font-semibold tabular-nums whitespace-nowrap">
                {fmtPrice(currentPrice)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Verdict */}
      <div className="text-center mb-5">
        {isToday ? (
          <p className="text-zinc-400 text-sm">
            Drag the slider to explore historical rates
          </p>
        ) : (
          <p className={`text-sm font-semibold tabular-nums ${isUp ? 'text-green-500' : 'text-red-500'}`}>
            {isUp ? '\u2191' : '\u2193'} {isUp ? 'Up' : 'Down'} {fmtDiff(diff)} since then ({isUp ? '+' : ''}{pctChange.toFixed(1)}%)
          </p>
        )}
      </div>

      {/* Slider */}
      <input
        type="range"
        aria-label="Historical price date selector"
        min={0}
        max={maxIdx}
        value={sliderValue}
        onChange={(e) => setSliderValue(Number(e.target.value))}
        className="w-full h-1 bg-zinc-800 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
      />
      <div className="flex items-center justify-between mt-2">
        <span className="text-zinc-400 text-[9px]">Today</span>
        <span className="text-zinc-400 text-[9px]">1 week</span>
        <span className="text-zinc-400 text-[9px]">1 month</span>
        <span className="text-zinc-400 text-[9px]">3 months</span>
        <span className="text-zinc-400 text-[9px]">1 year</span>
      </div>
    </div>
  )
}